import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';

import { api, ensureOk } from '@/api/client';
import { mensajeDe } from '@/api/errors';

import { AuthLayout, Campo } from './AuthLayout';

const schema = z
  .object({
    password: z.string().min(10, 'Usa al menos 10 caracteres').max(128),
    confirmar: z.string(),
  })
  .refine((v) => v.password === v.confirmar, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmar'],
  });
type Form = z.infer<typeof schema>;

/** Enlace de invitación o de recuperación del staff: `/definir-contrasena?token=…`. */
export function DefinirContrasenaPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const form = useForm<Form>({ resolver: zodResolver(schema) });
  const definir = useMutation({
    mutationFn: (v: Form) =>
      ensureOk(
        api.POST('/api/v1/admin/auth/password/set', { body: { token, password: v.password } }),
      ),
  });
  const { errors } = form.formState;

  if (definir.isSuccess) {
    return (
      <AuthLayout titulo="Contraseña lista">
        <div className="space-y-4 text-center">
          <CheckCircle2 className="mx-auto text-verde-texto" size={40} aria-hidden />
          <p className="text-texto-2">Ya puedes entrar al panel con tu correo y tu contraseña.</p>
          <Link to="/login" className="btn-cs btn-primario w-full">
            Iniciar sesión
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout titulo="Define tu contraseña">
      {!token ? (
        <p className="text-center text-texto-2">
          El enlace está incompleto. Ábrelo de nuevo desde el correo o{' '}
          <Link to="/recuperar">pide uno nuevo</Link>.
        </p>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => void form.handleSubmit((v) => definir.mutate(v))(e)}
          noValidate
        >
          <Campo etiqueta="Nueva contraseña" error={errors.password?.message}>
            <input
              type="password"
              autoComplete="new-password"
              className="input-vidrio"
              {...form.register('password')}
            />
          </Campo>
          <Campo etiqueta="Confírmala" error={errors.confirmar?.message}>
            <input
              type="password"
              autoComplete="new-password"
              className="input-vidrio"
              {...form.register('confirmar')}
            />
          </Campo>
          {definir.isError && (
            <p role="alert" className="text-sm font-semibold text-rojo-texto">
              {mensajeDe(definir.error)} <Link to="/recuperar">Pedir otro enlace</Link>
            </p>
          )}
          <button type="submit" className="btn-cs btn-primario w-full" disabled={definir.isPending}>
            {definir.isPending ? 'Guardando…' : 'Guardar contraseña'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
